import React, { useState, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import {
  Trophy,
  Upload,
  FileArchive,
  ChevronRight,
  ChevronDown,
  Image,
  Loader2,
  Download,
  RefreshCw,
  X,
  Check,
  AlertCircle,
  Clock,
  Zap,
  Trash2,
} from "lucide-react";
import { cn } from "@/lib/utils";

interface WinningAd {
  id: number;
  imageUrl: string;
  originalFilename: string | null;
  sourceBrand: string | null;
  brandId: number | null;
  competitorName: string | null;
  imageWidth: number | null;
  imageHeight: number | null;
  aspectRatio: string | null;
  fileSizeBytes: number | null;
  qualityScore: number | null;
  analysisStatus: string;
  niche: string | null;
  adStyle: string | null;
  adAngle: string | null;
  promptRecipe: string | null;
  daysLive: number | null;
  createdAt: string;
}

interface Stats {
  total: number;
  analyzed: number;
  pending: number;
  failed: number;
  byNiche: Record<string, number>;
  byStyle: Record<string, number>;
}

interface TreeData {
  tree: Array<{
    niche: string | null;
    styles: Array<{
      adStyle: string | null;
      ads: WinningAd[];
    }>;
  }>;
  unprocessed: WinningAd[];
}

const ANALYSIS_STATUSES = ["pending", "analyzing", "complete", "failed"];
const NICHES = [
  "beverage", "beauty", "ecommerce", "education", "fashion", "fintech", "fitness",
  "food", "gaming", "health", "health-supplements", "health-wellness", "home",
  "home-decor", "pets", "saas", "skincare", "sports", "travel",
];
const AD_STYLES = [
  "Problem-Solution", "Benefits", "Features", "Reviews", "Listicle", "Advertorial",
  "Us vs Them", "Education", "UGC Style", "Before & After", "Founder Story",
  "Social Proof", "Urgency", "Product Launch", "Sale", "How It Works",
  "Myth Busting", "Dark Mode",
];
const AD_ANGLES = [
  "Pain Point", "Social Proof", "Testimonial", "Authority", "Transformation",
  "Curiosity Gap", "Scarcity", "Risk Reversal", "Aspiration", "Identity",
  "Comparison", "Direct Response", "Education", "Contrarian", "Stats Hook",
  "FOMO", "Emotional", "Humor",
];

function StatusIcon({ status }: { status: string }) {
  if (status === "complete") return <Check className="h-3 w-3 text-green-700" />;
  if (status === "analyzing") return <Loader2 className="h-3 w-3 text-blue-700 animate-spin" />;
  if (status === "failed") return <AlertCircle className="h-3 w-3 text-red-700" />;
  return <Clock className="h-3 w-3 text-[#746F66]" />;
}

function StatCard({ label, value, color }: { label: string; value: number; color: string }) {
  return (
    <div className="bg-white border border-[#E5E2DC] rounded-xl px-4 py-3">
      <p className="text-[10px] font-semibold uppercase tracking-wider text-[#746F66]">{label}</p>
      <p className="text-2xl font-bold mt-1" style={{ color }}>{value}</p>
    </div>
  );
}

function UploadBar({ onUploaded }: { onUploaded: () => void }) {
  const [brand, setBrand] = useState("");
  const [uploading, setUploading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState<string | null>(null);
  const imgRef = useRef<HTMLInputElement>(null);
  const zipRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();

  const doUpload = async (formData: FormData) => {
    const url = formData.has("zip") ? "/api/admin/winning-ads/upload-zip" : "/api/admin/winning-ads/upload";
    const res = await fetch(url, { method: "POST", body: formData });
    if (!res.ok) {
      // v2.00 — non-JSON error bodies (nginx 413 HTML page, proxy errors)
      // used to crash json() with "Unexpected token <" — the "random JSON
      // error". Read text first, try JSON, fall back to a readable message.
      const text = await res.text();
      let msg = `Upload failed (HTTP ${res.status})`;
      try {
        const err = JSON.parse(text);
        msg = err.error || msg;
      } catch {
        if (res.status === 413) msg = "Upload rejected: batch too large for the server (200MB limit per request)";
      }
      throw new Error(msg);
    }
    return res.json();
  };

  // v2.00 — chunk big libraries automatically. nginx caps request bodies at
  // 50MB and multer caps 100 files/request; one giant "upload everything"
  // batch blew past both. Split into batches of ≤25 files AND ≤35MB, upload
  // sequentially, aggregate the results.
  const CHUNK_MAX_FILES = 25;
  const CHUNK_MAX_BYTES = 35 * 1024 * 1024;
  const chunkFiles = (files: File[]): File[][] => {
    const chunks: File[][] = [];
    let cur: File[] = [];
    let curBytes = 0;
    for (const f of files) {
      if (cur.length > 0 && (cur.length >= CHUNK_MAX_FILES || curBytes + f.size > CHUNK_MAX_BYTES)) {
        chunks.push(cur);
        cur = [];
        curBytes = 0;
      }
      cur.push(f);
      curBytes += f.size;
    }
    if (cur.length) chunks.push(cur);
    return chunks;
  };

  const handleImages = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const allFiles = Array.from(e.target.files ?? []);
    if (!allFiles.length) return;
    // v2.13 — a single file over the 50MB per-file server limit used to
    // become its own chunk and 413 the whole batch ("failed to upload on
    // 3rd batch"). Pre-skip oversized files with names, upload the rest.
    const oversize = allFiles.filter((f) => f.size > 48 * 1024 * 1024);
    const files = allFiles.filter((f) => f.size <= 48 * 1024 * 1024);
    if (oversize.length) {
      toast({ title: `${oversize.length} file(s) over 50MB skipped: ${oversize.slice(0, 3).map((f) => f.name).join(", ")}${oversize.length > 3 ? ", …" : ""}`, variant: "destructive" });
    }
    if (!files.length) return;
    setUploading(true);
    setUploadStatus(null);
    let totalUploaded = 0;
    let totalSkipped = 0;
    let firstSkipReason: string | null = null;
    let failedBatches = 0;
    try {
      const chunks = chunkFiles(files);
      for (let i = 0; i < chunks.length; i++) {
        setUploadStatus(`Uploading batch ${i + 1}/${chunks.length} (${chunks[i].length} files)…`);
        try {
          const fd = new FormData();
          chunks[i].forEach(f => fd.append("images", f));
          if (brand) fd.append("sourceBrand", brand);
          const result = await doUpload(fd);
          totalUploaded += result.uploaded ?? 0;
          totalSkipped += result.skipped ?? 0;
          if (!firstSkipReason && result.skippedReasons?.length) {
            firstSkipReason = result.skippedReasons[0].reason;
          }
          onUploaded(); // refresh grid as batches land
        } catch (batchErr: any) {
          failedBatches++;
          if (!firstSkipReason) firstSkipReason = batchErr.message;
        }
      }
      const parts = [`${totalUploaded} uploaded`];
      if (totalSkipped) parts.push(`${totalSkipped} skipped`);
      if (failedBatches) parts.push(`${failedBatches} batch(es) failed`);
      const summary = parts.join(", ") + (firstSkipReason ? ` — ${firstSkipReason}` : "") + (totalUploaded > 0 ? ". Analysis running." : "");
      setUploadStatus(summary);
      toast({ title: summary, variant: totalUploaded === 0 ? "destructive" : undefined });
    } finally {
      setUploading(false);
      if (imgRef.current) imgRef.current.value = "";
    }
  };

  const handleZip = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    // v2.00 — nginx rejects request bodies over 50MB with an HTML 413 the
    // client can't parse. Catch oversized ZIPs before sending; the image
    // picker auto-batches, so it has no size ceiling.
    if (file.size > 195 * 1024 * 1024) {
      toast({ title: "ZIP is over 195MB — the server rejects bodies over 200MB. Use 'Upload Images' instead (it batches automatically), or split the ZIP.", variant: "destructive" });
      if (zipRef.current) zipRef.current.value = "";
      return;
    }
    setUploading(true);
    setUploadStatus(null);
    try {
      const fd = new FormData();
      fd.append("zip", file);
      if (brand) fd.append("sourceBrand", brand);
      const result = await doUpload(fd);
      setUploadStatus(result.message);
      onUploaded();
      toast({ title: result.message, variant: result.uploaded === 0 ? "destructive" : undefined });
    } catch (err: any) {
      toast({ title: err.message, variant: "destructive" });
    } finally {
      setUploading(false);
      if (zipRef.current) zipRef.current.value = "";
    }
  };

  return (
    <div className="bg-white border border-[#E5E2DC] rounded-xl p-4 flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-3">
        <input
          type="text"
          value={brand}
          onChange={e => setBrand(e.target.value)}
          placeholder="Brand name (optional)"
          className="px-3 py-1.5 text-sm bg-white border border-[#E5E2DC] rounded-lg text-[#1A1A1A] placeholder:text-[#746F66] focus:outline-none focus:ring-1 focus:ring-amber-500 w-52"
          data-testid="input-winning-ads-brand"
        />
        <input type="file" ref={imgRef} accept="image/*" multiple className="hidden" onChange={handleImages} data-testid="input-winning-ads-images" />
        <input type="file" ref={zipRef} accept=".zip" className="hidden" onChange={handleZip} data-testid="input-winning-ads-zip" />
        <Button
          size="sm"
          onClick={() => imgRef.current?.click()}
          disabled={uploading}
          className="bg-[#1A1A1A] hover:bg-[#3D3A36] text-white"
          data-testid="button-upload-images"
        >
          <Upload className="h-3.5 w-3.5 mr-1.5" />
          Upload Images
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={() => zipRef.current?.click()}
          disabled={uploading}
          className="border-[#E5E2DC] text-[#3D3A36] hover:text-[#1A1A1A] hover:bg-[#F5F3EE]"
          data-testid="button-upload-zip"
        >
          <FileArchive className="h-3.5 w-3.5 mr-1.5" />
          Upload ZIP
        </Button>
        {uploading && <Loader2 className="h-4 w-4 animate-spin text-amber-700" />}
      </div>
      {uploadStatus && (
        <p className="text-xs text-amber-700" data-testid="text-upload-status">{uploadStatus}</p>
      )}
    </div>
  );
}

function AdDetailPanel({ ad, onClose, onSaved, onDeleted }: {
  ad: WinningAd;
  onClose: () => void;
  onSaved: () => void;
  onDeleted: () => void;
}) {
  const { toast } = useToast();
  const [form, setForm] = useState({
    imageUrl: ad.imageUrl,
    originalFilename: ad.originalFilename ?? "",
    sourceBrand: ad.sourceBrand ?? "",
    imageWidth: ad.imageWidth ?? "",
    imageHeight: ad.imageHeight ?? "",
    aspectRatio: ad.aspectRatio ?? "",
    fileSizeBytes: ad.fileSizeBytes ?? "",
    qualityScore: ad.qualityScore ?? 8,
    analysisStatus: ad.analysisStatus,
    niche: ad.niche ?? "",
    adStyle: ad.adStyle ?? "",
    adAngle: ad.adAngle ?? "",
    promptRecipe: ad.promptRecipe ?? "",
  });
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [reanalyzing, setReanalyzing] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const res = await fetch(`/api/admin/winning-ads/${ad.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          imageUrl: form.imageUrl || undefined,
          originalFilename: form.originalFilename || null,
          sourceBrand: form.sourceBrand || null,
          imageWidth: form.imageWidth !== "" ? Number(form.imageWidth) : null,
          imageHeight: form.imageHeight !== "" ? Number(form.imageHeight) : null,
          aspectRatio: form.aspectRatio || null,
          fileSizeBytes: form.fileSizeBytes !== "" ? Number(form.fileSizeBytes) : null,
          qualityScore: form.qualityScore,
          analysisStatus: form.analysisStatus,
          niche: form.niche || null,
          adStyle: form.adStyle || null,
          adAngle: form.adAngle || null,
          promptRecipe: form.promptRecipe || null,
        }),
      });
      if (!res.ok) throw new Error("Save failed");
      toast({ title: "Saved" });
      onSaved();
    } catch {
      toast({ title: "Failed to save", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const del = async () => {
    setDeleting(true);
    try {
      const res = await fetch(`/api/admin/winning-ads/${ad.id}`, { method: "DELETE" });
      if (!res.ok) throw new Error("Delete failed");
      toast({ title: "Deleted" });
      onDeleted();
    } catch {
      toast({ title: "Failed to delete", variant: "destructive" });
    } finally {
      setDeleting(false);
    }
  };

  const reanalyze = async () => {
    setReanalyzing(true);
    try {
      const res = await fetch(`/api/admin/winning-ads/${ad.id}/reanalyze`, { method: "POST" });
      if (!res.ok) throw new Error("Failed");
      toast({ title: "Queued for re-analysis" });
      onSaved();
    } catch {
      toast({ title: "Failed to queue", variant: "destructive" });
    } finally {
      setReanalyzing(false);
    }
  };

  const field = (label: string, key: keyof typeof form, type: "text" | "select" | "number" | "textarea", options?: string[]) => (
    <div className="flex flex-col gap-1">
      <label className="text-[10px] font-semibold uppercase tracking-wider text-[#746F66]">{label}</label>
      {type === "textarea" ? (
        <textarea
          value={form[key] as string}
          onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
          rows={5}
          className="px-2.5 py-1.5 text-xs bg-white border border-[#E5E2DC] rounded-lg text-[#1A1A1A] focus:outline-none focus:ring-1 focus:ring-amber-500 resize-y"
          data-testid={`input-winning-ad-${key}`}
        />
      ) : type === "select" ? (
        <select
          value={form[key] as string}
          onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
          className="px-2.5 py-1.5 text-xs bg-white border border-[#E5E2DC] rounded-lg text-[#1A1A1A] focus:outline-none focus:ring-1 focus:ring-amber-500"
          data-testid={`select-winning-ad-${key}`}
        >
          <option value="">— None —</option>
          {(() => {
            const currentVal = form[key] as string;
            const allOpts = options ?? [];
            const extra = currentVal && !allOpts.includes(currentVal) ? [currentVal] : [];
            return [...extra, ...allOpts].map(o => (
              <option key={o} value={o}>{o}{extra.includes(o) ? " (current)" : ""}</option>
            ));
          })()}
        </select>
      ) : (
        <input
          type={type}
          value={form[key] as string | number}
          onChange={e => setForm(f => ({ ...f, [key]: type === "number" ? Number(e.target.value) : e.target.value }))}
          className="px-2.5 py-1.5 text-xs bg-white border border-[#E5E2DC] rounded-lg text-[#1A1A1A] focus:outline-none focus:ring-1 focus:ring-amber-500"
          data-testid={`input-winning-ad-${key}`}
        />
      )}
    </div>
  );

  return (
    <div className="fixed inset-y-0 right-0 w-96 max-w-full bg-[#F5F3EE] border-l border-[#E5E2DC] z-50 flex flex-col shadow-2xl overflow-hidden">
      <div className="flex items-center justify-between px-4 py-3 border-b border-[#E5E2DC]">
        <div>
          <p className="text-sm font-semibold text-[#1A1A1A]">Ad #{ad.id}</p>
          <p className="text-[10px] text-[#746F66] truncate max-w-[280px]">{ad.originalFilename}</p>
        </div>
        <button onClick={onClose} className="p-1 rounded hover:bg-[#F5F3EE] text-[#746F66]" data-testid="button-close-detail-panel">
          <X className="h-4 w-4" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        <div className="aspect-video bg-white rounded-lg overflow-hidden border border-[#E5E2DC]">
          <img
            src={ad.imageUrl}
            alt={ad.originalFilename ?? "ad"}
            className="w-full h-full object-contain"
            data-testid="img-winning-ad-preview"
          />
        </div>

        <div className="grid grid-cols-3 gap-2 text-center">
          {ad.aspectRatio && (
            <div className="bg-white rounded-lg px-2 py-1.5">
              <p className="text-[9px] text-[#746F66] uppercase">Ratio</p>
              <p className="text-xs font-semibold text-[#1A1A1A]">{ad.aspectRatio}</p>
            </div>
          )}
          {ad.qualityScore != null && (
            <div className="bg-white rounded-lg px-2 py-1.5">
              <p className="text-[9px] text-[#746F66] uppercase">Quality</p>
              <p className="text-xs font-semibold text-amber-700">{ad.qualityScore}/10</p>
            </div>
          )}
          <div className="bg-white rounded-lg px-2 py-1.5">
            <p className="text-[9px] text-[#746F66] uppercase">Status</p>
            <div className="flex items-center justify-center gap-1">
              <StatusIcon status={ad.analysisStatus} />
              <p className="text-xs font-semibold text-[#1A1A1A] capitalize">{ad.analysisStatus}</p>
            </div>
          </div>
        </div>

        <div className="space-y-3">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-[#746F66] pt-1">File Metadata</p>
          {field("Image URL", "imageUrl", "text")}
          {field("Original Filename", "originalFilename", "text")}
          {field("Source Brand", "sourceBrand", "text")}
          <div className="grid grid-cols-2 gap-2">
            {field("Width (px)", "imageWidth", "number")}
            {field("Height (px)", "imageHeight", "number")}
          </div>
          {field("Aspect Ratio", "aspectRatio", "text")}
          {field("File Size (bytes)", "fileSizeBytes", "number")}
          <p className="text-[10px] font-semibold uppercase tracking-wider text-[#746F66] pt-1">Analysis Fields</p>
          {field("Quality Score (1–10)", "qualityScore", "number")}
          {field("Status", "analysisStatus", "select", ANALYSIS_STATUSES)}
          {field("Niche", "niche", "select", NICHES)}
          {field("Ad Style", "adStyle", "select", AD_STYLES)}
          {field("Ad Angle", "adAngle", "select", AD_ANGLES)}
          {field("Prompt Recipe", "promptRecipe", "textarea")}
          <div className="pt-1 text-[9px] text-[#746F66] space-y-0.5">
            <p>Created: {new Date(ad.createdAt).toLocaleString()}</p>
          </div>
        </div>
      </div>

      <div className="flex gap-2 p-4 border-t border-[#E5E2DC]">
        <Button size="sm" onClick={save} disabled={saving} className="flex-1 bg-[#1A1A1A] hover:bg-[#3D3A36] text-white" data-testid="button-save-winning-ad">
          {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5 mr-1" />}
          Save
        </Button>
        <Button size="sm" variant="outline" onClick={reanalyze} disabled={reanalyzing} className="border-[#E5E2DC] text-[#3D3A36] hover:text-[#1A1A1A]" data-testid="button-reanalyze-winning-ad">
          {reanalyzing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
        </Button>
        <Button size="sm" variant="outline" onClick={del} disabled={deleting} className="border-red-800 text-red-700 hover:bg-red-50" data-testid="button-delete-winning-ad">
          {deleting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <X className="h-3.5 w-3.5" />}
        </Button>
      </div>
    </div>
  );
}

function AdRow({ ad, onClick, showDetail }: { ad: WinningAd; onClick: () => void; showDetail?: boolean }) {
  return (
    <button
      onClick={onClick}
      className="w-full flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-[#F5F3EE] transition-colors text-left group"
      data-testid={`row-winning-ad-${ad.id}`}
    >
      <div className="h-8 w-8 rounded bg-white overflow-hidden flex-shrink-0">
        <img src={ad.imageUrl} alt="" className="h-full w-full object-cover" onError={e => { (e.target as HTMLImageElement).style.display = "none"; }} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-xs text-[#1A1A1A] font-medium">#{ad.id}</span>
          <span className="text-[10px] text-[#746F66] truncate">{ad.originalFilename}</span>
        </div>
        <div className="flex items-center gap-2 mt-0.5 flex-wrap">
          {ad.aspectRatio && <span className="text-[9px] text-[#746F66]">{ad.aspectRatio}</span>}
          {ad.qualityScore != null && <span className="text-[9px] text-amber-600">Q:{ad.qualityScore}</span>}
          {showDetail && ad.adStyle && (
            <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-white text-[#3D3A36]">{ad.adStyle}</span>
          )}
          {showDetail && ad.daysLive != null && (
            <span className="text-[9px] text-[#746F66]">{ad.daysLive}d live</span>
          )}
          {showDetail && (
            <span className="text-[9px] text-[#746F66] capitalize">{ad.analysisStatus}</span>
          )}
        </div>
      </div>
      <div className="flex-shrink-0">
        <StatusIcon status={ad.analysisStatus} />
      </div>
    </button>
  );
}

function TreeBrowser({ data, onSelectAd, onDeleteNiche }: { data: TreeData; onSelectAd: (ad: WinningAd) => void; onDeleteNiche: (niche: string, count: number) => void }) {
  const [openNiches, setOpenNiches] = useState<Set<string>>(new Set());
  const [openStyles, setOpenStyles] = useState<Set<string>>(new Set());
  const [unprocessedOpen, setUnprocessedOpen] = useState(true);

  const toggleNiche = (n: string) => setOpenNiches(s => { const ns = new Set(s); ns.has(n) ? ns.delete(n) : ns.add(n); return ns; });
  const toggleStyle = (k: string) => setOpenStyles(s => { const ns = new Set(s); ns.has(k) ? ns.delete(k) : ns.add(k); return ns; });

  return (
    <div className="space-y-1">
      {data.tree.map(nicheGroup => {
        const nicheKey = nicheGroup.niche ?? "_unknown";
        const isOpen = openNiches.has(nicheKey);
        const nicheAdCount = nicheGroup.styles.reduce((a, s) => a + s.ads.length, 0);
        return (
          <div key={nicheKey}>
            <div className="flex items-center">
              <button
                onClick={() => toggleNiche(nicheKey)}
                className="flex-1 flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-[#F5F3EE] transition-colors text-left"
                data-testid={`tree-niche-${nicheKey}`}
              >
                {isOpen ? <ChevronDown className="h-3.5 w-3.5 text-[#746F66] flex-shrink-0" /> : <ChevronRight className="h-3.5 w-3.5 text-[#746F66] flex-shrink-0" />}
                <span className="text-xs font-semibold text-[#3D3A36] capitalize">{nicheGroup.niche ?? "Unknown Niche"}</span>
                <span className="text-[10px] text-[#746F66] ml-auto">{nicheAdCount}</span>
              </button>
              {nicheGroup.niche && (
                <button
                  onClick={e => { e.stopPropagation(); onDeleteNiche(nicheGroup.niche!, nicheAdCount); }}
                  className="p-1.5 rounded hover:bg-red-50 text-[#746F66] hover:text-red-700 transition-colors flex-shrink-0"
                  data-testid={`button-delete-niche-${nicheKey}`}
                  title={`Delete all ads in ${nicheGroup.niche}`}
                >
                  <Trash2 className="h-3 w-3" />
                </button>
              )}
            </div>
            {isOpen && nicheGroup.styles.map(styleGroup => {
              const styleKey = `${nicheKey}/${styleGroup.adStyle ?? "_unknown"}`;
              const sOpen = openStyles.has(styleKey);
              return (
                <div key={styleKey} className="ml-4">
                  <button
                    onClick={() => toggleStyle(styleKey)}
                    className="w-full flex items-center gap-2 px-3 py-1.5 rounded-lg hover:bg-[#F5F3EE] transition-colors text-left"
                    data-testid={`tree-style-${styleKey}`}
                  >
                    {sOpen ? <ChevronDown className="h-3 w-3 text-[#746F66] flex-shrink-0" /> : <ChevronRight className="h-3 w-3 text-[#746F66] flex-shrink-0" />}
                    <span className="text-[11px] text-[#746F66]">{styleGroup.adStyle ?? "Unknown Style"}</span>
                    <span className="text-[9px] text-[#746F66] ml-auto">{styleGroup.ads.length}</span>
                  </button>
                  {sOpen && styleGroup.ads.map(ad => (
                    <div key={ad.id} className="ml-4">
                      <AdRow ad={ad} onClick={() => onSelectAd(ad)} />
                    </div>
                  ))}
                </div>
              );
            })}
          </div>
        );
      })}

      {data.unprocessed.length > 0 && (
        <div>
          <button
            onClick={() => setUnprocessedOpen(o => !o)}
            className="w-full flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-[#F5F3EE] transition-colors text-left"
            data-testid="tree-unprocessed"
          >
            {unprocessedOpen ? <ChevronDown className="h-3.5 w-3.5 text-[#746F66] flex-shrink-0" /> : <ChevronRight className="h-3.5 w-3.5 text-[#746F66] flex-shrink-0" />}
            <span className="text-xs font-semibold text-[#746F66]">Unprocessed</span>
            <span className="text-[10px] text-[#746F66] ml-auto">{data.unprocessed.length}</span>
          </button>
          {unprocessedOpen && data.unprocessed.map(ad => (
            <div key={ad.id} className="ml-4">
              <AdRow ad={ad} onClick={() => onSelectAd(ad)} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

interface CompetitorStyleGroup {
  adStyle: string | null;
  ads: WinningAd[];
}

interface CompetitorGroup {
  competitorKey?: string;
  identityVerified?: boolean;
  sourceBrandId?: number | null;
  competitorName: string | null;
  count: number;
  styles: CompetitorStyleGroup[];
}

interface BrandGroup {
  libraryKey?: string;
  sourceBrandIds?: number[];
  brandId: number | null;
  brandName: string | null;
  totalCount: number;
  competitors: CompetitorGroup[];
}

interface CompetitorTreeData {
  tree: BrandGroup[];
}

function CompetitorTreeBrowser({ data, onSelectAd }: { data: CompetitorTreeData; onSelectAd: (ad: WinningAd) => void }) {
  const [openBrands, setOpenBrands] = useState<Set<string>>(new Set());
  const [openCompetitors, setOpenCompetitors] = useState<Set<string>>(new Set());
  const [openStyles, setOpenStyles] = useState<Set<string>>(new Set());

  const toggleBrand = (k: string) => setOpenBrands(s => { const ns = new Set(s); ns.has(k) ? ns.delete(k) : ns.add(k); return ns; });
  const toggleCompetitor = (k: string) => setOpenCompetitors(s => { const ns = new Set(s); ns.has(k) ? ns.delete(k) : ns.add(k); return ns; });
  const toggleStyle = (k: string) => setOpenStyles(s => { const ns = new Set(s); ns.has(k) ? ns.delete(k) : ns.add(k); return ns; });

  if (data.tree.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <div className="h-14 w-14 rounded-2xl bg-white border border-[#E5E2DC] flex items-center justify-center mb-4">
          <Trophy className="h-7 w-7 text-[#746F66]" />
        </div>
        <p className="text-sm text-[#746F66]">No competitor ads found</p>
        <p className="text-xs text-[#746F66] mt-1 max-w-xs">Competitor ads are ingested automatically from brand competitor scans.</p>
      </div>
    );
  }

  return (
    <div className="space-y-1">
      {data.tree.map(brandGroup => {
        const brandKey = brandGroup.libraryKey ?? String(brandGroup.brandId ?? "_unknown");
        const isOpen = openBrands.has(brandKey);
        return (
          <div key={brandKey}>
            <button
              onClick={() => toggleBrand(brandKey)}
              className="w-full flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-[#F5F3EE] transition-colors text-left"
              data-testid={`tree-brand-${brandKey}`}
            >
              {isOpen ? <ChevronDown className="h-3.5 w-3.5 text-[#746F66] flex-shrink-0" /> : <ChevronRight className="h-3.5 w-3.5 text-[#746F66] flex-shrink-0" />}
              <span className="text-xs font-semibold text-[#3D3A36]">{brandGroup.brandName ?? "Unknown Brand"}</span>
              <span className="text-[10px] text-[#746F66] ml-1">({brandGroup.totalCount} ads)</span>
            </button>
            {isOpen && brandGroup.competitors.map(competitor => {
              const competitorKey = `${brandKey}/${competitor.competitorKey ?? competitor.competitorName ?? "_unknown"}`;
              const cOpen = openCompetitors.has(competitorKey);
              return (
                <div key={competitorKey} className="ml-4">
                  <button
                    onClick={() => toggleCompetitor(competitorKey)}
                    className="w-full flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-[#F5F3EE] transition-colors text-left"
                    data-testid={`tree-competitor-${competitorKey}`}
                  >
                    {cOpen ? <ChevronDown className="h-3.5 w-3.5 text-[#746F66] flex-shrink-0" /> : <ChevronRight className="h-3.5 w-3.5 text-[#746F66] flex-shrink-0" />}
                    <span className="text-xs font-semibold text-[#3D3A36]">{competitor.competitorName ?? "Unknown Competitor"}</span>
                    {!competitor.identityVerified && (brandGroup.sourceBrandIds?.length ?? 0) > 1 && <span className="text-[10px] text-[#746F66]" title="This historical scan has no verified advertiser ID, so its ads remain separate.">Scan {competitor.sourceBrandId}</span>}
                    <span className="text-[10px] text-[#746F66] ml-auto">{competitor.count}</span>
                  </button>
                  {cOpen && competitor.styles.map(styleGroup => {
                    const styleKey = `${competitorKey}/${styleGroup.adStyle ?? "_unknown"}`;
                    const sOpen = openStyles.has(styleKey);
                    return (
                      <div key={styleKey} className="ml-4">
                        <button
                          onClick={() => toggleStyle(styleKey)}
                          className="w-full flex items-center gap-2 px-3 py-1.5 rounded-lg hover:bg-[#F5F3EE] transition-colors text-left"
                          data-testid={`tree-style-${styleKey}`}
                        >
                          {sOpen ? <ChevronDown className="h-3 w-3 text-[#746F66] flex-shrink-0" /> : <ChevronRight className="h-3 w-3 text-[#746F66] flex-shrink-0" />}
                          <span className="text-[11px] text-[#746F66]">{styleGroup.adStyle ?? "Unknown Style"}</span>
                          <span className="text-[9px] text-[#746F66] ml-auto">{styleGroup.ads.length}</span>
                        </button>
                        {sOpen && styleGroup.ads.map(ad => (
                          <div key={ad.id} className="ml-4">
                            <AdRow ad={ad} onClick={() => onSelectAd(ad)} showDetail />
                          </div>
                        ))}
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        );
      })}
    </div>
  );
}

export default function WinningAdsPage({ initialTab = "platform" }: { initialTab?: "platform" | "competitor" }) {
  const qc = useQueryClient();
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState<"platform" | "competitor">(initialTab);
  const [selectedAd, setSelectedAd] = useState<WinningAd | null>(null);
  const [analyzingPending, setAnalyzingPending] = useState(false);
  const [reanalyzingAll, setReanalyzingAll] = useState(false);
  const [purging, setPurging] = useState(false);
  const [showPurgeConfirm, setShowPurgeConfirm] = useState(false);

  const { data: stats, isLoading: statsLoading } = useQuery<Stats>({
    queryKey: ["/api/admin/winning-ads/stats", activeTab],
    queryFn: async () => {
      const res = await fetch(`/api/admin/winning-ads/stats?scope=${activeTab}`);
      if (!res.ok) throw new Error("Failed to fetch stats");
      return res.json();
    },
    staleTime: 5_000,
    refetchInterval: (query) => {
      const data = query.state.data as Stats | undefined;
      return data && data.pending > 0 ? 10_000 : false;
    },
  });

  const { data: treeData, isLoading: treeLoading } = useQuery<TreeData>({
    queryKey: ["/api/admin/winning-ads/tree", "platform"],
    queryFn: async () => {
      const res = await fetch("/api/admin/winning-ads/tree?scope=platform");
      if (!res.ok) throw new Error("Failed to fetch tree");
      return res.json();
    },
    staleTime: 15_000,
    enabled: activeTab === "platform",
  });

  const { data: competitorTreeData, isLoading: competitorTreeLoading } = useQuery<CompetitorTreeData>({
    queryKey: ["/api/admin/winning-ads/tree", "competitor"],
    queryFn: async () => {
      const res = await fetch("/api/admin/winning-ads/tree?scope=competitor");
      if (!res.ok) throw new Error("Failed to fetch competitor tree");
      return res.json();
    },
    staleTime: 15_000,
    enabled: activeTab === "competitor",
  });

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["/api/admin/winning-ads/stats", activeTab] });
    if (activeTab === "platform") {
      qc.invalidateQueries({ queryKey: ["/api/admin/winning-ads/tree", "platform"] });
    } else {
      qc.invalidateQueries({ queryKey: ["/api/admin/winning-ads/tree", "competitor"] });
    }
  };

  const handleAnalyzePending = async () => {
    setAnalyzingPending(true);
    try {
      const res = await fetch("/api/admin/winning-ads/analyze-pending", { method: "POST" });
      if (!res.ok) throw new Error("Failed");
      const data = await res.json();
      toast({ title: data.message ?? `${data.queued} ads queued for analysis` });
      refresh();
    } catch {
      toast({ title: "Failed to queue", variant: "destructive" });
    } finally {
      setAnalyzingPending(false);
    }
  };

  const handleReanalyzeAll = async () => {
    const total = stats?.total ?? 0;
    const estimatedCost = (total * 0.02).toFixed(2);
    const confirmed = window.confirm(
      `Re-analyze all ${total} ads?\nEstimated cost: $${estimatedCost}`
    );
    if (!confirmed) return;
    setReanalyzingAll(true);
    try {
      const res = await fetch("/api/admin/winning-ads/reanalyze-all", { method: "POST" });
      if (!res.ok) throw new Error("Failed");
      const data = await res.json();
      toast({ title: `${data.queued} ads queued for re-analysis` });
      refresh();
    } catch {
      toast({ title: "Failed to queue", variant: "destructive" });
    } finally {
      setReanalyzingAll(false);
    }
  };

  const handleDeleteNiche = async (niche: string, count: number) => {
    const confirmed = window.confirm(`Delete all ${count} ads in "${niche}"?`);
    if (!confirmed) return;
    try {
      const res = await fetch("/api/admin/winning-ads/bulk", {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ niche }),
      });
      if (!res.ok) throw new Error("Failed");
      const data = await res.json();
      toast({ title: `Deleted ${data.deleted} ads from "${niche}"` });
      refresh();
    } catch {
      toast({ title: "Failed to delete", variant: "destructive" });
    }
  };

  const handlePurgeAll = async () => {
    setPurging(true);
    setShowPurgeConfirm(false);
    try {
      const res = await fetch("/api/admin/winning-ads/purge-all", { method: "DELETE" });
      if (!res.ok) throw new Error("Failed");
      const data = await res.json();
      toast({ title: `Purged ${data.deleted} ads and ${data.filesDeleted} image files` });
      setSelectedAd(null);
      refresh();
    } catch {
      toast({ title: "Purge failed", variant: "destructive" });
    } finally {
      setPurging(false);
    }
  };

  const handleExportCsv = async () => {
    try {
      const res = await fetch("/api/admin/winning-ads/export-csv", { credentials: "include" });
      if (!res.ok) {
        toast({ title: `Export failed (HTTP ${res.status})`, variant: "destructive" });
        return;
      }
      const blob = await res.blob();
      const cd = res.headers.get("content-disposition") ?? "";
      const match = cd.match(/filename="?([^";]+)"?/i);
      const filename = match?.[1] ?? `winning-ads-${new Date().toISOString().slice(0, 10)}.csv`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast({ title: `Exported ${filename}` });
    } catch (err: any) {
      toast({ title: "Export failed", description: err?.message, variant: "destructive" });
    }
  };

  const nicheColors: Record<string, string> = {
    beverage: "#0E7490",
    beauty: "#BE185D",
    ecommerce: "#C2410C",
    education: "#4338CA",
    fashion: "#BE123C",
    fintech: "#B45309",
    fitness: "#1D4ED8",
    food: "#4D7C0F",
    gaming: "#7E22CE",
    health: "#047857",
    "health-supplements": "#047857",
    "health-wellness": "#047857",
    home: "#B45309",
    "home-decor": "#B45309",
    pets: "#C2410C",
    saas: "#6D28D9",
    skincare: "#BE185D",
    sports: "#2563EB",
    travel: "#0F766E",
  };

  const styleColors: Record<string, string> = {
    "Problem-Solution": "#B91C1C",
    "Benefits": "#047857",
    "Features": "#1D4ED8",
    "Reviews": "#B45309",
    "Listicle": "#6D28D9",
    "Advertorial": "#6B7280",
    "Us vs Them": "#BE185D",
    "Education": "#4338CA",
    "UGC Style": "#C2410C",
    "Before & After": "#0F766E",
    "Founder Story": "#4D7C0F",
    "Social Proof": "#A16207",
    "Urgency": "#DC2626",
    "Product Launch": "#7C3AED",
    "Sale": "#BE123C",
    "How It Works": "#0369A1",
    "Myth Busting": "#B45309",
    "Dark Mode": "#374151",
  };

  return (
    <div className="flex flex-col gap-5 relative">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[#1A1A1A] flex items-center gap-2">
            <Trophy className="h-6 w-6 text-amber-700" />
            Winning Ads Library
          </h1>
          <p className="text-sm text-[#746F66] mt-0.5">Upload and manage winning ad creatives for AI calibration</p>
        </div>
      </div>

      <div className="flex gap-1 bg-white border border-[#E5E2DC] rounded-lg p-1 w-fit">
        <button
          onClick={() => setActiveTab("platform")}
          className={cn(
            "px-4 py-1.5 text-sm font-medium rounded-md transition-colors",
            activeTab === "platform"
              ? "bg-[#1A1A1A] text-white"
              : "text-[#746F66] hover:text-[#1A1A1A] hover:bg-[#F5F3EE]"
          )}
          data-testid="tab-winning-ads"
        >
          Winning Ads
        </button>
        <button
          onClick={() => setActiveTab("competitor")}
          className={cn(
            "px-4 py-1.5 text-sm font-medium rounded-md transition-colors",
            activeTab === "competitor"
              ? "bg-[#1A1A1A] text-white"
              : "text-[#746F66] hover:text-[#1A1A1A] hover:bg-[#F5F3EE]"
          )}
          data-testid="tab-competitor-ads"
        >
          Competitor Ads
        </button>
      </div>

      {activeTab === "platform" && <UploadBar onUploaded={refresh} />}

      <div className="flex flex-col gap-3">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <StatCard label="Total" value={statsLoading ? 0 : stats?.total ?? 0} color="#1A1A1A" />
          <StatCard label="Analyzed" value={statsLoading ? 0 : stats?.analyzed ?? 0} color="#047857" />
          <StatCard label="Pending" value={statsLoading ? 0 : stats?.pending ?? 0} color="#B45309" />
          <StatCard label="Failed" value={statsLoading ? 0 : stats?.failed ?? 0} color="#B91C1C" />
        </div>

        {stats && (
          <div className="space-y-2">
            {Object.keys(stats.byNiche).length > 0 && (
              <div className="flex flex-wrap gap-1.5 items-center">
                <span className="text-[10px] font-semibold uppercase tracking-wider text-[#746F66] mr-1">Niches:</span>
                {Object.entries(stats.byNiche)
                  .sort((a, b) => b[1] - a[1])
                  .map(([niche, count]) => (
                    <span
                      key={niche}
                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium capitalize"
                      style={{ backgroundColor: (nicheColors[niche] ?? "#6B7280") + "30", color: nicheColors[niche] ?? "#4B5563", border: `1px solid ${(nicheColors[niche] ?? "#6B7280")}50` }}
                      data-testid={`pill-niche-${niche}`}
                    >
                      {niche} <span className="opacity-70">{count}</span>
                    </span>
                  ))}
              </div>
            )}
            {Object.keys(stats.byStyle).length > 0 && (
              <div className="flex flex-wrap gap-1.5 items-center border-t border-[#E5E2DC] pt-2">
                <span className="text-[10px] font-semibold uppercase tracking-wider text-[#746F66] mr-1">Styles:</span>
                {Object.entries(stats.byStyle)
                  .sort((a, b) => b[1] - a[1])
                  .map(([style, count]) => (
                    <span
                      key={style}
                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium"
                      style={{ backgroundColor: (styleColors[style] ?? "#6B7280") + "25", color: styleColors[style] ?? "#4B5563", border: `1px solid ${(styleColors[style] ?? "#6B7280")}45` }}
                      data-testid={`pill-style-${style}`}
                    >
                      {style} <span className="opacity-70">{count}</span>
                    </span>
                  ))}
              </div>
            )}
          </div>
        )}

        <div className="flex items-center gap-2 flex-wrap">
          <Button
            size="sm"
            variant="outline"
            onClick={handleAnalyzePending}
            disabled={analyzingPending}
            className="border-[#E5E2DC] text-[#3D3A36] hover:text-[#1A1A1A] hover:bg-[#F5F3EE]"
            data-testid="button-reanalyze-failed"
          >
            {analyzingPending ? (
              <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
            ) : (
              <Zap className="h-3.5 w-3.5 mr-1.5 text-amber-700" />
            )}
            Analyze Pending & Failed
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={handleReanalyzeAll}
            disabled={reanalyzingAll}
            className="border-[#E5E2DC] text-[#3D3A36] hover:text-[#1A1A1A] hover:bg-[#F5F3EE]"
            data-testid="button-reanalyze-all"
          >
            {reanalyzingAll ? (
              <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
            ) : (
              <RefreshCw className="h-3.5 w-3.5 mr-1.5" />
            )}
            Re-analyze All
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={handleExportCsv}
            className="border-[#E5E2DC] text-[#3D3A36] hover:text-[#1A1A1A] hover:bg-[#F5F3EE]"
            data-testid="button-export-csv"
          >
            <Download className="h-3.5 w-3.5 mr-1.5" />
            Export CSV
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => setShowPurgeConfirm(true)}
            disabled={purging || (stats?.total ?? 0) === 0}
            className="border-red-800 text-red-700 hover:text-[#1A1A1A] hover:bg-red-50 ml-auto"
            data-testid="button-purge-all"
          >
            {purging ? (
              <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
            ) : (
              <Trash2 className="h-3.5 w-3.5 mr-1.5" />
            )}
            Purge All
          </Button>
        </div>
      </div>

      {showPurgeConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
          <div className="bg-white border border-red-800 rounded-xl p-6 max-w-sm w-full mx-4 shadow-2xl">
            <div className="flex items-center gap-3 mb-3">
              <div className="p-2 rounded-lg bg-red-900/40">
                <Trash2 className="h-5 w-5 text-red-700" />
              </div>
              <h3 className="text-base font-semibold text-[#1A1A1A]">Purge All Winning Ads</h3>
            </div>
            <p className="text-sm text-[#746F66] mb-1">
              This will permanently delete <span className="text-[#1A1A1A] font-semibold">{stats?.total ?? 0} ads</span> and their image files from disk.
            </p>
            <p className="text-xs text-red-700 mb-5">This action cannot be undone.</p>
            <div className="flex gap-3">
              <Button
                size="sm"
                variant="outline"
                onClick={() => setShowPurgeConfirm(false)}
                className="flex-1 border-[#E5E2DC] text-[#3D3A36] hover:text-[#1A1A1A] hover:bg-[#F5F3EE]"
                data-testid="button-purge-cancel"
              >
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={handlePurgeAll}
                className="flex-1 bg-red-700 hover:bg-red-600 text-white border-0"
                data-testid="button-purge-confirm"
              >
                Yes, Purge All
              </Button>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white border border-[#E5E2DC] rounded-xl overflow-hidden">
        <div className="flex items-center gap-2 px-4 py-3 border-b border-[#E5E2DC]">
          <Image className="h-4 w-4 text-amber-700" />
          <span className="text-sm font-semibold text-[#1A1A1A]">Ad Browser</span>
          {(stats?.total ?? 0) > 0 && <span className="text-xs text-[#746F66]">({stats!.total} total)</span>}
        </div>

        <div className="p-3">
          {activeTab === "platform" ? (
            treeLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-6 w-6 animate-spin text-amber-700" />
              </div>
            ) : !treeData || (treeData.tree.length === 0 && treeData.unprocessed.length === 0) ? (
              <div className="flex flex-col items-center justify-center py-16 text-center">
                <div className="h-14 w-14 rounded-2xl bg-white border border-[#E5E2DC] flex items-center justify-center mb-4">
                  <Trophy className="h-7 w-7 text-[#746F66]" />
                </div>
                <p className="text-sm text-[#746F66]">No winning ads yet</p>
                <p className="text-xs text-[#746F66] mt-1 max-w-xs">Upload images or a ZIP file to get started. AI will analyze and categorize them automatically.</p>
              </div>
            ) : (
              <TreeBrowser data={treeData} onSelectAd={setSelectedAd} onDeleteNiche={handleDeleteNiche} />
            )
          ) : (
            competitorTreeLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-6 w-6 animate-spin text-amber-700" />
              </div>
            ) : (
              <CompetitorTreeBrowser data={competitorTreeData ?? { tree: [] }} onSelectAd={setSelectedAd} />
            )
          )}
        </div>
      </div>

      {selectedAd && (
        <AdDetailPanel
          ad={selectedAd}
          onClose={() => setSelectedAd(null)}
          onSaved={() => {
            refresh();
            setSelectedAd(null);
          }}
          onDeleted={() => {
            refresh();
            setSelectedAd(null);
          }}
        />
      )}
    </div>
  );
}
